// Órdenes de trabajo — listado con filtros (estado, tipo, búsqueda), orden por
// columnas y creación vía dialog (RHF + Zod del schema compartido).
//
// El filtro por TIPO es client-side: el backend solo filtra estado/activo/q y
// tipo_mantenimiento es texto libre.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import {
  ordenTrabajoMttoCreateSchema,
  type EstadoOt,
  type OrdenTrabajoMtto,
  type OrdenTrabajoMttoCreate,
} from 'schemas';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { TBody, TD, TH, THead, TR, Table } from '../../components/ui/table';
import { Textarea } from '../../components/ui/textarea';
import { cn } from '../../lib/utils';
import { CargandoModulo, ErrorModulo, MantenimientoShell } from './MantenimientoShell';
import { useActivosMtto, useCrearOrdenMtto, useOrdenesMtto } from './queries';
import {
  BannerSinRolMtto,
  ESTADOS_OT,
  ESTADO_OT_LABEL,
  EstadoOtBadge,
  ORIGEN_OT_LABEL,
  TIPOS_MANTENIMIENTO,
  esCodigoApi,
  fmtFecha,
  fmtFechaHora,
  mensajeError,
  otVencida,
  tipoMantenimientoLabel,
  usePermisosMtto,
} from './shared';

type OrdenKey = 'codigo' | 'activo' | 'fecha' | 'creada';
type Direccion = 'asc' | 'desc';

export function OrdenesTrabajoPage() {
  const navigate = useNavigate();
  const { puedeOperar } = usePermisosMtto();
  const [estado, setEstado] = useState<EstadoOt | ''>('');
  const [tipo, setTipo] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [orden, setOrden] = useState<{ key: OrdenKey; dir: Direccion }>({
    key: 'creada',
    dir: 'desc',
  });
  const [crearAbierto, setCrearAbierto] = useState(false);

  const { data, isLoading, isError, error } = useOrdenesMtto({
    ...(estado ? { estado } : {}),
    ...(busqueda.trim() ? { q: busqueda.trim() } : {}),
  });

  const filas = useMemo(() => {
    let rows = data ?? [];
    if (tipo) {
      rows = rows.filter((ot) =>
        tipo === 'otro'
          ? ot.tipo_mantenimiento !== 'preventivo' && ot.tipo_mantenimiento !== 'correctivo'
          : ot.tipo_mantenimiento === tipo,
      );
    }
    const factor = orden.dir === 'asc' ? 1 : -1;
    const valor = (ot: OrdenTrabajoMtto): string => {
      switch (orden.key) {
        case 'codigo':
          return ot.codigo ?? '';
        case 'activo':
          return ot.activo_nombre ?? '';
        case 'fecha':
          return ot.fecha_programada ?? '';
        case 'creada':
          return ot.created_at;
      }
    };
    return [...rows].sort((a, b) => valor(a).localeCompare(valor(b)) * factor);
  }, [data, tipo, orden]);

  const toggleOrden = (key: OrdenKey) =>
    setOrden((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    );

  const flecha = (key: OrdenKey) => (orden.key === key ? (orden.dir === 'asc' ? ' ↑' : ' ↓') : '');

  return (
    <MantenimientoShell
      titulo="Órdenes de trabajo"
      subtitulo={data ? `${filas.length} orden(es) con los filtros actuales` : undefined}
      acciones={
        puedeOperar ? <Button onClick={() => setCrearAbierto(true)}>Crear orden</Button> : undefined
      }
    >
      <Card>
        <CardContent className="grid grid-cols-1 gap-3 p-4 pt-4 md:grid-cols-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor="ot-estado">Estado</Label>
            <Select
              id="ot-estado"
              value={estado}
              onChange={(e) => setEstado(e.target.value as EstadoOt | '')}
            >
              <option value="">Todos</option>
              {ESTADOS_OT.map((e) => (
                <option key={e} value={e}>
                  {ESTADO_OT_LABEL[e]}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="ot-tipo">Tipo</Label>
            <Select id="ot-tipo" value={tipo} onChange={(e) => setTipo(e.target.value)}>
              <option value="">Todos</option>
              <option value="preventivo">Preventivo</option>
              <option value="correctivo">Correctivo</option>
              <option value="otro">Otro / sin tipo</option>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="ot-q">Buscar</Label>
            <Input
              id="ot-q"
              placeholder="Código o descripción…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {isLoading ? <CargandoModulo mensaje="Cargando órdenes de trabajo…" /> : null}
      {isError ? <ErrorModulo error={error} /> : null}

      {data && filas.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-fg-tertiary">
            Sin órdenes de trabajo con esos filtros.
          </CardContent>
        </Card>
      ) : null}

      {filas.length > 0 ? (
        <Table>
          <THead>
            <TR>
              <TH>
                <button type="button" className="font-medium" onClick={() => toggleOrden('codigo')}>
                  Código{flecha('codigo')}
                </button>
              </TH>
              <TH>
                <button type="button" className="font-medium" onClick={() => toggleOrden('activo')}>
                  Activo{flecha('activo')}
                </button>
              </TH>
              <TH>Descripción</TH>
              <TH align="center">Tipo</TH>
              <TH align="center">Estado</TH>
              <TH align="right">
                <button type="button" className="font-medium" onClick={() => toggleOrden('fecha')}>
                  Programada{flecha('fecha')}
                </button>
              </TH>
              <TH align="right">
                <button type="button" className="font-medium" onClick={() => toggleOrden('creada')}>
                  Creada{flecha('creada')}
                </button>
              </TH>
            </TR>
          </THead>
          <TBody>
            {filas.map((ot) => {
              const vencida = otVencida(ot);
              return (
                <TR
                  key={ot.id}
                  className="cursor-pointer hover:bg-surface-overlay"
                  onClick={() => navigate(`/mantenimiento/ordenes/${ot.id}`)}
                >
                  <TD className="font-mono text-xs text-fg">{ot.codigo ?? '—'}</TD>
                  <TD className="text-xs text-fg-secondary">{ot.activo_nombre ?? '—'}</TD>
                  <TD className="max-w-md truncate text-xs text-fg">{ot.descripcion}</TD>
                  <TD align="center" className="text-xs text-fg-secondary">
                    {tipoMantenimientoLabel(ot.tipo_mantenimiento)}
                  </TD>
                  <TD align="center">
                    <EstadoOtBadge estado={ot.estado} />
                  </TD>
                  <TD
                    align="right"
                    className={cn(
                      'text-xs tabular-nums',
                      vencida ? 'font-medium text-crit' : 'text-fg-secondary',
                    )}
                  >
                    {fmtFecha(ot.fecha_programada)}
                    {vencida ? (
                      <Badge tone="crit" className="ml-2">
                        Vencida
                      </Badge>
                    ) : null}
                  </TD>
                  <TD align="right" className="text-xs tabular-nums text-fg-tertiary">
                    {fmtFechaHora(ot.created_at)}
                  </TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
      ) : null}

      <CrearOrdenDialog open={crearAbierto} onClose={() => setCrearAbierto(false)} />
    </MantenimientoShell>
  );
}

// =============================================================================
// Crear orden — RHF + zodResolver sobre el contrato compartido.
// =============================================================================

function CrearOrdenDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const crear = useCrearOrdenMtto();
  const activos = useActivosMtto();
  const [sinRol, setSinRol] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<OrdenTrabajoMttoCreate>({
    resolver: zodResolver(ordenTrabajoMttoCreateSchema),
    defaultValues: { origen: 'manual' },
  });

  const cerrar = () => {
    reset();
    setSinRol(false);
    onClose();
  };

  const onSubmit = handleSubmit(async (values) => {
    try {
      const creada = await crear.mutateAsync(values);
      toast.success(`Orden ${creada.codigo ?? ''} creada`);
      cerrar();
      navigate(`/mantenimiento/ordenes/${creada.id}`);
    } catch (err) {
      if (esCodigoApi(err, 'rol_mantenimiento_no_autorizado')) {
        setSinRol(true);
        return;
      }
      toast.error(mensajeError(err));
    }
  });

  return (
    <Dialog open={open} onClose={cerrar} className="max-w-lg">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Crear orden de trabajo</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            void onSubmit(e);
          }}
          className="flex flex-col gap-3"
        >
          {sinRol ? <BannerSinRolMtto accion="crear órdenes de trabajo" /> : null}
          <div className="flex flex-col gap-1">
            <Label htmlFor="ot-crear-activo">Activo *</Label>
            <Select
              id="ot-crear-activo"
              aria-invalid={Boolean(errors.activo_id)}
              {...register('activo_id')}
            >
              <option value="">Seleccione un activo…</option>
              {(activos.data ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.codigo ? `${a.codigo} · ` : ''}
                  {a.nombre}
                </option>
              ))}
            </Select>
            {errors.activo_id ? (
              <p className="text-xs text-crit">Seleccione el activo a intervenir</p>
            ) : null}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor="ot-crear-origen">Origen *</Label>
              <Select id="ot-crear-origen" {...register('origen')}>
                {(['manual', 'plan', 'solicitud'] as const).map((o) => (
                  <option key={o} value={o}>
                    {ORIGEN_OT_LABEL[o]}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="ot-crear-tipo">Tipo de mantenimiento</Label>
              <Select
                id="ot-crear-tipo"
                {...register('tipo_mantenimiento', {
                  setValueAs: (v: string) => (v === '' ? undefined : v),
                })}
              >
                <option value="">Sin tipo</option>
                {TIPOS_MANTENIMIENTO.map((t) => (
                  <option key={t} value={t}>
                    {tipoMantenimientoLabel(t)}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="ot-crear-desc">Descripción *</Label>
            <Textarea
              id="ot-crear-desc"
              rows={3}
              placeholder="Describa el trabajo a realizar…"
              aria-invalid={Boolean(errors.descripcion)}
              {...register('descripcion')}
            />
            {errors.descripcion ? (
              <p className="text-xs text-crit">Describa el trabajo (máximo 500 caracteres)</p>
            ) : null}
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="ot-crear-fecha">Fecha programada</Label>
            <Input
              id="ot-crear-fecha"
              type="date"
              aria-invalid={Boolean(errors.fecha_programada)}
              {...register('fecha_programada', {
                setValueAs: (v: string) => (v === '' ? undefined : v),
              })}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={cerrar}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creando…' : 'Crear orden'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
